import { LoadingContainer } from '@/components/ui';
import type React from 'react';
import { useEmpresas } from '@/hooks/useEmpresas';
import '@/pages/admin/admin.css';
import type { Empresa } from '@/types/types';
import { getAsesoriaEstadoBadgeVariant, getAsesoriaEstadoLabel, normalizeAsesoriaEstado } from '@/utils/asesoriaStatus';
import { Building2, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

function formatHours(decimalHours: number): string {
  const totalSeconds = Math.round(decimalHours * 3600);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return '—';
  try {
    // "19 oct 2026" en vez de "19 de oct de 2026": cabe en la columna de la tarjeta en móvil.
    return new Date(dateStr).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ de /g, ' ');
  } catch {
    return '—';
  }
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
}

function chipColorClass(variant: string): string {
  if (variant === 'green') return 'bg-[#eaf5ee] text-[#2d7a4f]';
  if (variant === 'red') return 'bg-[#fdf0ee] text-[#c0392b]';
  if (variant === 'amber' || variant === 'yellow') return 'bg-[#fdf3e0] text-[#b56b00]';
  if (variant === 'blue') return 'bg-[#eaf1fb] text-[#1e5fa8]';
  if (variant === 'purple') return 'bg-[#f0edfc] text-[#5a38c4]';
  return 'bg-[#f2f1ee] text-[#666]';
}

function getCumplimientoColor(v: number): string {
  if (v >= 80) return 'var(--admin-green)';
  if (v >= 50) return 'var(--admin-amber)';
  return 'var(--admin-red)';
}

/** Meta de horas de asesoría por empresa (misma que el detalle de empresa del admin). */
const HORAS_META = 320;

/** Ancho de columnas de la lista (cabecera y filas comparten la plantilla). */
const COLUMNAS = 'md:grid-cols-[minmax(0,1.6fr)_minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,0.9fr)_16px]';

function BarraMini({ porcentaje, color }: { porcentaje: number; color: string }) {
  return (
    <div className="h-1.5 rounded-full mt-1.5" style={{ background: 'var(--admin-border)' }}>
      <div className="h-1.5 rounded-full" style={{ width: `${Math.min(Math.max(porcentaje, 0), 100)}%`, background: color }} />
    </div>
  );
}

function Etiqueta({ children }: { children: React.ReactNode }) {
  return (
    <div className="md:hidden text-[10px] uppercase tracking-wider font-medium mb-0.5" style={{ color: 'var(--admin-text3)' }}>
      {children}
    </div>
  );
}

/** Las que necesitan atención primero: correcciones, luego en revisión; el resto en su orden. */
function prioridad(e: Empresa): number {
  if ((e.cantidadAsesoriasConCorreccionesPendientes ?? 0) > 0) return 0;
  if (e.asesoriaActivaEstado === 'EN_REVISION') return 1;
  return 2;
}

function EmpresaFila({ empresa }: { empresa: Empresa }) {
  const estadoAsesoria = empresa.asesoriaActivaEstado ? normalizeAsesoriaEstado({ estado: empresa.asesoriaActivaEstado }) : null;
  const cumplimiento = empresa.cumplimiento ?? 0;
  const correcciones = empresa.cantidadAsesoriasConCorreccionesPendientes ?? 0;
  // Sin los campos nuevos (backend anterior) se cae a las horas propias del asesor.
  const horasEmpresa = empresa.horasAprobadasEmpresa ?? empresa.horasAsesoria;
  const porAprobar = empresa.horasPorAprobarEmpresa ?? empresa.horasAsesoriaActiva ?? 0;
  const deOtros = empresa.horasDeOtrosAsesores ?? 0;
  const normas = empresa.combinacion?.nombre?.split(' + ').join(', ');

  return (
    <Link
      to={`/empresa/${empresa.nit}`}
      className={`group grid grid-cols-2 ${COLUMNAS} gap-x-6 gap-y-3 items-start md:items-center px-4 py-3.5 transition-colors hover:bg-(--admin-surface2)`}
    >
      {/* Empresa */}
      <div className="col-span-2 md:col-span-1 flex items-center gap-3 min-w-0">
        <div
          className="admin-avatar shrink-0"
          style={{ background: 'var(--admin-surface2)', color: 'var(--admin-text2)', width: 34, height: 34, fontSize: 12 }}
        >
          {getInitials(empresa.nombre)}
        </div>
        <div className="min-w-0">
          <div className="text-[13px] font-semibold truncate" style={{ color: 'var(--admin-text)' }} title={empresa.nombre}>
            {empresa.nombre}
          </div>
          <div className="text-[11px] truncate" style={{ color: 'var(--admin-text3)' }}>
            NIT {empresa.nit}
          </div>
          {normas && (
            <div className="text-[11px] font-medium mt-0.5" style={{ color: 'var(--admin-blue)' }}>
              {normas}
            </div>
          )}
        </div>
      </div>

      {/* Horas */}
      <div className="min-w-0">
        <Etiqueta>Horas aprobadas</Etiqueta>
        <div className="text-[12px] tabular-nums" style={{ color: 'var(--admin-text2)' }}>
          <span className="font-semibold" style={{ color: 'var(--admin-text)' }}>
            {formatHours(horasEmpresa)}
          </span>{' '}
          de {HORAS_META}h
        </div>
        <BarraMini porcentaje={(horasEmpresa * 100) / HORAS_META} color="var(--admin-blue)" />
        {porAprobar > 0 && (
          <div className="text-[11px] mt-1" style={{ color: 'var(--admin-amber)' }}>
            +{formatHours(porAprobar)} por aprobar
          </div>
        )}
        {deOtros > 0 && (
          <div
            className="text-[11px] mt-0.5"
            style={{ color: 'var(--admin-text3)' }}
            title="Horas hechas por el asesor anterior de la empresa"
          >
            Incluye {formatHours(deOtros)} de otro asesor
          </div>
        )}
      </div>

      {/* Cumplimiento */}
      <div className="min-w-0">
        <Etiqueta>Cumplimiento</Etiqueta>
        <div className="text-[12px] font-semibold tabular-nums" style={{ color: getCumplimientoColor(cumplimiento) }}>
          {cumplimiento.toLocaleString('es-CO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%
        </div>
        <BarraMini porcentaje={cumplimiento} color={getCumplimientoColor(cumplimiento)} />
      </div>

      {/* Estado */}
      <div className="col-span-2 md:col-span-1 flex flex-wrap md:flex-col items-center md:items-start gap-x-2 gap-y-1 min-w-0">
        {correcciones > 0 ? (
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${chipColorClass('red')}`}>
            {correcciones === 1 ? '1 por corregir' : `${correcciones} por corregir`}
          </span>
        ) : (
          estadoAsesoria && (
            <span
              className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${chipColorClass(getAsesoriaEstadoBadgeVariant(estadoAsesoria))}`}
            >
              {getAsesoriaEstadoLabel(estadoAsesoria)}
            </span>
          )
        )}
        <span className="text-[11px]" style={{ color: 'var(--admin-text3)' }}>
          {empresa.ultimaAsesoria ? `Última: ${formatDate(empresa.ultimaAsesoria)}` : 'Sin asesorías'}
        </span>
      </div>

      <ChevronRight
        className="hidden md:block w-4 h-4 transition-transform group-hover:translate-x-0.5"
        style={{ color: 'var(--admin-text3)' }}
      />
    </Link>
  );
}

export function EmpresaListPage() {
  const { empresas, isLoading, error } = useEmpresas();

  if (isLoading) return <LoadingContainer text="Cargando empresas..." />;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
      <div className="mb-6">
        <h1 className="text-[20px] font-semibold" style={{ color: 'var(--admin-text)' }}>
          Mis empresas
        </h1>
        <p className="text-[13px] mt-0.5" style={{ color: 'var(--admin-text3)' }}>
          {empresas.length > 0
            ? `${empresas.length} empresa${empresas.length !== 1 ? 's' : ''} asignada${empresas.length !== 1 ? 's' : ''}`
            : 'Sin empresas asignadas'}
        </p>
      </div>

      {error ? (
        <div className="admin-card p-12 text-center">
          <Building2 className="w-10 h-10 mx-auto mb-3" style={{ color: 'var(--admin-text3)' }} />
          <p className="text-[13px] font-medium" style={{ color: 'var(--admin-red)' }}>
            {error}
          </p>
        </div>
      ) : empresas.length === 0 ? (
        <div className="admin-card p-12 text-center">
          <Building2 className="w-10 h-10 mx-auto mb-3" style={{ color: 'var(--admin-text3)' }} />
          <p className="text-[13px] font-medium" style={{ color: 'var(--admin-text)' }}>
            Sin empresas asignadas
          </p>
          <p className="text-[12px] mt-1" style={{ color: 'var(--admin-text3)' }}>
            Contacta al administrador
          </p>
        </div>
      ) : (
        <div className="admin-card overflow-hidden">
          <div
            className={`hidden md:grid ${COLUMNAS} gap-x-6 px-4 py-2.5 text-[10px] uppercase tracking-wider font-medium border-b`}
            style={{ color: 'var(--admin-text3)', borderColor: 'var(--admin-border)', background: 'var(--admin-surface)' }}
          >
            <span>Empresa</span>
            <span>Horas aprobadas</span>
            <span>Cumplimiento</span>
            <span>Estado</span>
            <span />
          </div>
          <div className="divide-y divide-(--admin-border)">
            {[...empresas]
              .sort((a, b) => prioridad(a) - prioridad(b))
              .map((empresa) => (
                <EmpresaFila key={empresa.nit} empresa={empresa} />
              ))}
          </div>
        </div>
      )}
    </div>
  );
}
